import { captureEvent } from "@/lib/analytics";

/**
 * Conversion funnel steps, ordered from top (discovery) to bottom (purchase).
 *
 * Each step emits exactly one event so GA4 can build a funnel and surface
 * drop-off / cart abandonment by step. This is the analytics baseline the CRO
 * checklist depends on ("Cart abandonment tracked by step", "Baseline funnel
 * conversion documented before making changes").
 *
 * Keep these event names stable — renaming an event silently breaks the
 * historical funnel.
 */
export const FunnelEvent = {
  /** User submitted a property address from the hero/dashboard search. */
  AddressSubmitted: "funnel_address_submitted",
  /** User clicked a paid package and was sent to checkout. */
  CheckoutStarted: "funnel_checkout_started",
  /** User chose the free preview report instead of a paid package. */
  FreePreviewSelected: "funnel_free_preview_selected",
  /** The checkout page rendered with a valid order. */
  CheckoutViewed: "funnel_checkout_viewed",
  /** Stripe checkout session was created successfully. */
  PaymentSessionCreated: "funnel_payment_session_created",
  /** Stripe checkout session failed to initialize. */
  PaymentSessionFailed: "funnel_payment_session_failed",
  /** Stripe reported the embedded checkout as complete (payment submitted). */
  PaymentCompleted: "funnel_payment_completed",
  /** Server confirmed the payment and credited the account (true conversion). */
  PurchaseConfirmed: "funnel_purchase_confirmed",

  // --- Post-purchase -------------------------------------------------------
  // The funnel used to end above, which left the most important question
  // unanswerable: does the free preview convert because the product is good, or
  // because it is free? A delivered report nobody opened delivered nothing.
  //
  // These are the *acquisition-side* mirror of the same steps. Activation
  // itself is measured server-side (report_orders.first_opened_at and friends)
  // because these events are consent-gated and would under-count by an unknown
  // margin — see docs/analytics/funnel.md.

  /** Owner opened a report they own. */
  ReportOpened: "funnel_report_opened",
  /** Owner reached the negotiating brief — the B2C activation moment. */
  NegotiationBriefViewed: "funnel_negotiation_brief_viewed",
  /** Owner downloaded the report PDF. */
  ReportPdfDownloaded: "funnel_report_pdf_downloaded",
  /** Owner clicked to unlock a locked preview (free → paid intent). */
  ReportUnlockClicked: "funnel_report_unlock_clicked",
  /** Owner minted a public share link. */
  ReportShared: "funnel_report_shared",
  /** Someone opened a shared report link (the report as a distribution channel). */
  SharedReportViewed: "funnel_shared_report_viewed",
} as const;

export type FunnelEventName = (typeof FunnelEvent)[keyof typeof FunnelEvent];

export interface FunnelProps {
  packageName?: string;
  credits?: number;
  /** Amount in the smallest currency unit (cents), as carried through checkout. */
  amountCents?: number;
  currency?: string;
  propertyType?: string;
  /** Where the step was triggered (e.g. "hero", "dashboard"). */
  source?: string;
  hasAddress?: boolean;
  /** Report order the event belongs to, for post-purchase steps. */
  orderId?: string;
  /** Whether the report was a locked free preview at the time. */
  isPreview?: boolean;
  [key: string]: unknown;
}

/**
 * Emit a single funnel-step event. Derives a human-readable `amountEur` from
 * `amountCents` so funnels and revenue breakdowns work without per-call math.
 * SSR-safe — delegates to `captureEvent`, which no-ops on the server.
 *
 * Note for GA4: custom parameters need registering as custom dimensions/metrics
 * in the property before they show up in reports (they are collected regardless).
 */
export const trackFunnel = (event: FunnelEventName, props?: FunnelProps) => {
  if (!props) {
    captureEvent(event);
    return;
  }

  const { amountCents } = props;
  captureEvent(event, {
    ...props,
    ...(amountCents !== undefined ? { amountEur: amountCents / 100 } : {}),
  });
};

/**
 * Engagement signal for a primary call-to-action click (top-of-funnel intent,
 * upstream of the funnel steps above). `cta` is a stable identifier such as
 * "hero_order" so clicks can be compared across placements.
 */
export const trackCtaClick = (cta: string, props?: Record<string, unknown>) => {
  captureEvent("cta_clicked", { cta, ...props });
};
